// Lazy route modules with their CSS (ADR 0004, SPEC §9.2). esbuild emits a
// CSS file per dynamically imported chunk but never loads it; node/html.mjs
// writes assets/css-manifest.json mapping each chunk's source entry to its
// CSS. lazyRoute() attaches and awaits that <link> before the route renders,
// so there is no flash of unstyled content.
import type { ComponentType } from 'react'
import type { LazyRouteFunction, RouteObject } from 'react-router-dom'

type Manifest = Record<string, string>

let manifest: Promise<Manifest> | undefined

function loadManifest(): Promise<Manifest> {
  manifest ??= fetch(`${import.meta.env.APP_BASE_PATH}assets/css-manifest.json`)
    .then((res) => (res.ok ? (res.json() as Promise<Manifest>) : {}))
    .catch(() => ({}))
  return manifest
}

const loaded = new Map<string, Promise<void>>()

/** Attach the stylesheet for a lazily imported entry (once) and wait for it. */
export function loadEntryCss(entry: string): Promise<void> {
  let pending = loaded.get(entry)
  if (!pending) {
    pending = loadManifest().then(
      (map) =>
        new Promise<void>((resolve) => {
          const href = map[entry]
          if (!href) {
            resolve()
            return
          }
          const link = document.createElement('link')
          link.rel = 'stylesheet'
          link.href = href
          link.dataset.entry = entry
          // A missing stylesheet must not block the route forever.
          link.addEventListener('load', () => {
            resolve()
          })
          link.addEventListener('error', () => {
            resolve()
          })
          document.head.appendChild(link)
        })
    )
    loaded.set(entry, pending)
  }
  return pending
}

/**
 * `lazy` for a route: `entry` is the imported file's path from the repo root
 * (e.g. 'src/features/modules/routes.tsx') — the key esbuild writes in the
 * metafile. Keep it in step with the import() path.
 */
/** What a lazily loaded route module may provide (react-router's own type). */
export type LazyRouteModule = Awaited<ReturnType<LazyRouteFunction<RouteObject>>>

export function lazyRoute(
  entry: string,
  importer: () => Promise<LazyRouteModule>
): LazyRouteFunction<RouteObject> {
  return async () => {
    const [module] = await Promise.all([importer(), loadEntryCss(entry)])
    return module
  }
}

/** Test helper. */
export function resetLazyCss() {
  manifest = undefined
  loaded.clear()
}

/**
 * One lazily loaded pages module serving several routes: returns a factory
 * that picks a page component by export name. All routes share one chunk.
 */
export function lazyPages<M extends Record<string, unknown>>(
  entry: string,
  importer: () => Promise<M>
) {
  return (exportName: keyof M & string): LazyRouteFunction<RouteObject> =>
    lazyRoute(entry, async () => {
      const module = await importer()
      const Component = module[exportName] as ComponentType | undefined
      if (!Component) throw new Error(`${entry} has no export "${exportName}"`)
      return { Component }
    })
}
